import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, User, Mail, Calendar, Package, DollarSign, Building2, Phone, LogOut } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useLots } from '../../../contexts/LotsContext';
import { useSales } from '../../../contexts/SalesContext';
import CreateBusiness from '../../../components/business/CreateBusiness';
import BusinessDashboard from '../../../components/business/BusinessDashboard';
import BusinessSwitcher from '../../../components/business/BusinessSwitcher';
import { supabase } from '../../../services/supabase';

const ProfileScreen = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { lots } = useLots();
  const { sales } = useSales();
  const [isEditingWhatsapp, setIsEditingWhatsapp] = useState(false);
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || '');
  const [showCreateBusiness, setShowCreateBusiness] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from('profiles')
        .select('name, surname')
        .eq('id', user.id)
        .single();
      
      if (!error && data) {
        setProfile(data);
      }
    };
    
    fetchProfile();
  }, [user]);

  console.log('User debug:', {
    user,
    provider: user?.app_metadata?.provider,
    identities: user?.identities,
    raw_app_meta: user?.app_metadata,
    raw_user_meta: user?.user_metadata,
  });

  // Estadísticas básicas
  const stats = {
    totalLots: lots?.length || 0,
    totalSales: sales?.length || 0
  };

  const handleWhatsappSubmit = async (e) => {
    e.preventDefault();
    // TODO: Implementar la actualización del whatsapp
    setIsEditingWhatsapp(false);
  };

  const handleBusinessCreated = (business) => {
    setShowCreateBusiness(false);
    // Refresh the page to update user data
    window.location.reload();
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="flex items-center p-4">
          <button 
            onClick={() => navigate(-1)}
            className="mr-4 text-gray-900 dark:text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Mi Perfil</h1>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Foto y nombre */}
        <div className="flex flex-col items-center">
          {user?.user_metadata?.avatar_url ? (
            <img 
              src={user.user_metadata.avatar_url}
              alt="Foto de perfil" 
              className="w-24 h-24 rounded-full shadow-lg object-cover"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <User className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
          )}
          <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
            {profile ? `${profile.name} ${profile.surname}` : user?.email}
          </h2>
        </div>

        {/* Business Switcher */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <BusinessSwitcher />
        </div>

        {/* Información del usuario */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-4 border-b dark:border-gray-700 flex items-center">
            <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Email</div>
              <div className="text-gray-900 dark:text-white">{user?.email}</div>
            </div>
          </div>
          <div className="p-4 border-b dark:border-gray-700 flex items-center">
            <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Miembro desde</div>
              <div className="text-gray-900 dark:text-white">{user?.created_at ? new Date(user.created_at).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              }) : 'N/A'}</div>
            </div>
          </div>
          <div className="p-4 border-b dark:border-gray-700">
            <div className="flex items-center mb-4">
              <Building2 className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
              <div className="text-sm text-gray-500 dark:text-gray-400">Negocio</div>
            </div>
            {user?.business_id ? (
              <BusinessDashboard businessId={user.business_id} />
            ) : showCreateBusiness ? (
              <CreateBusiness onBusinessCreated={handleBusinessCreated} />
            ) : (
              <button
                onClick={() => setShowCreateBusiness(true)}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
              >
                Tocá para crear tu negocio
              </button>
            )}
          </div>
          <div className="p-4 flex items-center">
            <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
            <div className="flex-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">WhatsApp</div>
              {user?.whatsapp ? (
                <div className="text-gray-900 dark:text-white">{user.whatsapp}</div>
              ) : isEditingWhatsapp ? (
                <form onSubmit={handleWhatsappSubmit} className="mt-1">
                  <input
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="Ingresa tu número de WhatsApp"
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                  />
                  <div className="mt-2 flex space-x-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-700 dark:hover:bg-blue-600"
                    >
                      Guardar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingWhatsapp(false)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setIsEditingWhatsapp(true)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm mt-1"
                >
                  Tocá para agregar tu WhatsApp
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-5 h-5 text-green-500 dark:text-green-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Lotes</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalLots}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Ventas</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSales}</div>
          </div>
        </div>

        {/* Cerrar sesión */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </div>
  );
};

export default ProfileScreen; 